import { useLocation } from 'react-router-dom';
import {
  BellRing, BookOpen, Building2, CalendarDays, ChartColumn, CreditCard, GraduationCap, LayoutGrid, LifeBuoy,
  MessageCircleQuestion, MessagesSquare, Settings, TicketPercent, TriangleAlert, UserPlus, Users,
} from 'lucide-react';
import { useI18n } from '../../i18n/I18nContext';
import { usePermissions } from '../../hooks/usePermissions';

/**
 * What the shell navigates to, in one place: the rail, the phone drawer and the tab bar all read
 * it, so they can never disagree about where you are or what a role may open.
 */

// A destination stays highlighted on every route it owns (sub-routes and legacy redirects included).
const OWNED_PATHS = {
  '/students': ['/students', '/mentor/students'],
  '/leads': ['/leads'],
  '/reminders': ['/reminders'],
  '/sales': ['/sales', '/order-lists', '/discount', '/invoice'],
  '/inbox': ['/inbox', '/messages', '/direct-messages', '/feedback'],
  '/settings/mentor': ['/settings', '/mentor/profile'],
  '/team': ['/team', '/teams'],
  '/teacher/dashboard': ['/teacher/dashboard', '/teacher/students', '/teacher/quiz-feedback'],
  '/teacher/qa': ['/teacher/qa', '/teacher/courses'],
};

export function isNavPathActive(pathname, path) {
  const owned = OWNED_PATHS[path] || [path];
  return owned.some((p) => pathname === p || pathname.startsWith(`${p}/`));
}

const STAFF_ITEMS = {
  dashboard: { to: '/dashboard', labelKey: 'nav.home', Icon: LayoutGrid },
  courses: { to: '/courses', labelKey: 'nav.courses', Icon: BookOpen },
  students: { to: '/students', labelKey: 'nav.students', Icon: GraduationCap },
  clients: { to: '/clients', labelKey: 'nav.clients', Icon: Building2 },
  leads: { to: '/leads', labelKey: 'nav.leads', Icon: UserPlus },
  reminders: { to: '/reminders', labelKey: 'nav.reminders', Icon: BellRing },
  sales: { to: '/sales', labelKey: 'nav.sales', Icon: CreditCard },
  promocodes: { to: '/promocodes', labelKey: 'nav.promocodes', Icon: TicketPercent },
  inbox: { to: '/inbox', labelKey: 'nav.inbox', Icon: MessagesSquare },
  support: { to: '/support', labelKey: 'layout.nav.support', shortKey: 'layout.nav.supportShort', Icon: LifeBuoy },
  analytics: { to: '/statistics', labelKey: 'nav.analytics', Icon: ChartColumn },
  calendar: { to: '/calendar', labelKey: 'nav.calendar', Icon: CalendarDays },
  team: { to: '/team', labelKey: 'nav.team', Icon: Users },
  settings: { to: '/settings/mentor', labelKey: 'nav.settings', Icon: Settings },
};

// Grouped like the platform's rail: a few primary destinations, then commerce and talk, then
// insight, then the admin and account corners. Filtered by the staff member's effective role.
const STAFF_GROUPS = [
  { key: 'workspace', labelKey: 'nav.workspace', items: ['dashboard', 'courses', 'students', 'clients'] },
  { key: 'operations', labelKey: 'nav.operations', items: ['leads', 'reminders', 'sales', 'promocodes', 'inbox', 'support'] },
  { key: 'insights', labelKey: 'nav.insights', items: ['analytics', 'calendar'] },
  { key: 'admin', labelKey: 'nav.admin', items: ['team'], adminOnly: true },
  { key: 'account', labelKey: 'nav.account', items: ['settings'] },
];

// The phone tab bar shows four of these (then "More"): what each role reaches first.
const TAB_PRIORITY = ['dashboard', 'students', 'courses', 'sales', 'leads', 'reminders', 'inbox', 'clients', 'promocodes', 'support', 'analytics', 'calendar'];

const TEACHER_ITEMS = [
  { key: 'students', to: '/teacher/dashboard', labelKey: 'layout.nav.myStudents', Icon: GraduationCap },
  { key: 'atRisk', to: '/teacher/at-risk', labelKey: 'layout.nav.atRisk', Icon: TriangleAlert },
  { key: 'metrics', to: '/teacher/metrics', labelKey: 'layout.nav.metrics', Icon: ChartColumn },
  { key: 'qa', to: '/teacher/qa', labelKey: 'layout.nav.qa', Icon: MessageCircleQuestion },
];

function buildItem(key, def, pathname, t) {
  return {
    key,
    to: def.to,
    label: t(def.labelKey),
    shortLabel: def.shortKey ? t(def.shortKey) : t(def.labelKey),
    Icon: def.Icon,
    active: isNavPathActive(pathname, def.to),
  };
}

/** The staff (admin / sales / call-centre ...) navigation for the signed-in role. */
export function useStaffNav() {
  const { pathname } = useLocation();
  const { t } = useI18n();
  const { canSeeNav, isAdmin } = usePermissions();
  const admin = isAdmin();

  const groups = STAFF_GROUPS
    .filter((group) => !group.adminOnly || admin)
    .map((group) => ({
      key: group.key,
      label: t(group.labelKey),
      items: group.items
        .filter((key) => key === 'team' || key === 'settings' || canSeeNav(key))
        .map((key) => buildItem(key, STAFF_ITEMS[key], pathname, t)),
    }))
    .filter((group) => group.items.length > 0);

  const tabs = TAB_PRIORITY
    .filter((key) => canSeeNav(key))
    .slice(0, 4)
    .map((key) => buildItem(key, STAFF_ITEMS[key], pathname, t));

  return { groups, tabs, hasMore: true };
}

/** The teacher area's four destinations. */
export function useTeacherNav() {
  const { pathname } = useLocation();
  const { t } = useI18n();
  const items = TEACHER_ITEMS.map((def) => buildItem(def.key, def, pathname, t));
  return { groups: [{ key: 'main', label: t('nav.main'), items }], tabs: items, hasMore: false };
}
